<template>
  <div class="toast-layer">
    <div
      v-for="position in positions"
      :key="position"
      class="toast-stack"
      :class="`toast-stack--${position}`"
    >
      <div
        v-for="toast in toastsByPosition(position)"
        :key="toast.id"
        class="toast"
        :class="[
          `toast--${toast.type}`,
          `toast--${toast.position}`,
          {
            'is-visible': toast.visible,
            'is-leaving': toast.leaving,
          },
        ]"
        role="status"
        aria-live="polite"
      >
        <div class="toast__badge">
          <svg
            v-if="toast.type === 'success'"
            class="toast__icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M20 6 9 17l-5-5"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
            />
          </svg>
          <span v-else>!</span>
        </div>
        <div class="toast__content">
          <strong v-if="toast.title" class="toast__title">{{ toast.title }}</strong>
          <span class="toast__message">{{ toast.message }}</span>
        </div>
        <button class="toast__close" type="button" @click="dismissToast(toast.id)">
          &times;
        </button>
        <span
          v-if="toast.duration > 0"
          class="toast__progress"
          :style="{ animationDuration: `${toast.duration}ms` }"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
const TOAST_ENTER_DELAY = 40;
const TOAST_DURATION = 5000;
const TOAST_EXIT_DURATION = 480;

export default {
  name: 'ToastNotifications',
  data() {
    return {
      nextToastId: 0,
      positions: [
        'top-center',
        'top-left',
        'top-right',
        'bottom-center',
        'bottom-left',
        'bottom-right',
      ],
      toasts: [],
    };
  },
  mounted() {
    this.$root.$on('toast', this.enqueueToast);
    this.$root.$on('toast:clear', this.clearToasts);
  },
  beforeDestroy() {
    this.$root.$off('toast', this.enqueueToast);
    this.$root.$off('toast:clear', this.clearToasts);
    this.clearToasts();
  },
  methods: {
    toastsByPosition(position) {
      return this.toasts.filter((toast) => toast.position === position);
    },
    enqueueToast(payload = {}) {
      const toastPayload =
        typeof payload === 'string' ? { message: payload } : payload;
      const toast = {
        id: this.nextToastId++,
        type: toastPayload.type || 'success',
        title: toastPayload.title || '',
        message: toastPayload.message || '',
        position: toastPayload.position || 'bottom-left',
        duration:
          typeof toastPayload.duration === 'number'
            ? toastPayload.duration
            : TOAST_DURATION,
        visible: false,
        leaving: false,
        enterTimeoutId: null,
        timeoutId: null,
        removalTimeoutId: null,
      };

      if (!toast.message) {
        return;
      }

      this.toasts.push(toast);

      this.$nextTick(() => {
        toast.enterTimeoutId = window.setTimeout(() => {
          toast.visible = true;
        }, TOAST_ENTER_DELAY);
      });

      if (toast.duration > 0) {
        toast.timeoutId = window.setTimeout(() => {
          this.dismissToast(toast.id);
        }, toast.duration);
      }
    },
    dismissToast(toastId) {
      const toast = this.toasts.find((item) => item.id === toastId);

      if (!toast || toast.leaving) {
        return;
      }

      if (toast.timeoutId) {
        window.clearTimeout(toast.timeoutId);
      }

      if (toast.enterTimeoutId) {
        window.clearTimeout(toast.enterTimeoutId);
      }

      toast.leaving = true;
      toast.visible = false;
      toast.removalTimeoutId = window.setTimeout(() => {
        this.removeToast(toastId);
      }, TOAST_EXIT_DURATION);
    },
    removeToast(toastId) {
      const index = this.toasts.findIndex((item) => item.id === toastId);

      if (index === -1) {
        return;
      }

      const [toast] = this.toasts.splice(index, 1);

      if (toast.timeoutId) {
        window.clearTimeout(toast.timeoutId);
      }

      if (toast.enterTimeoutId) {
        window.clearTimeout(toast.enterTimeoutId);
      }

      if (toast.removalTimeoutId) {
        window.clearTimeout(toast.removalTimeoutId);
      }
    },
    clearToasts() {
      this.toasts.forEach((toast) => {
        if (toast.timeoutId) {
          window.clearTimeout(toast.timeoutId);
        }

        if (toast.enterTimeoutId) {
          window.clearTimeout(toast.enterTimeoutId);
        }

        if (toast.removalTimeoutId) {
          window.clearTimeout(toast.removalTimeoutId);
        }
      });

      this.toasts = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.toast-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 20000;
}

.toast-stack {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(360px, calc(100vw - 32px));

  &--top-center {
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    align-items: stretch;
  }

  &--top-left {
    top: 20px;
    left: 20px;
    align-items: stretch;
  }

  &--top-right {
    top: 20px;
    right: 20px;
    align-items: stretch;
  }

  &--bottom-center {
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    align-items: stretch;
  }

  &--bottom-left {
    left: 20px;
    bottom: 20px;
    align-items: stretch;
  }

  &--bottom-right {
    right: 20px;
    bottom: 20px;
    align-items: stretch;
  }

}

.toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
  padding: 14px 14px 14px 12px;
  border-radius: 14px;
  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 245, 245, 0.96));
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(14px);
  pointer-events: auto;
  opacity: 0;
  transition:
    opacity 0.24s ease,
    transform 0.46s cubic-bezier(0.22, 1, 0.36, 1);

  &--top-center {
    transform: translate3d(0, calc(-100% - 32px), 0);
  }

  &--bottom-center {
    transform: translate3d(0, calc(100% + 32px), 0);
  }

  &--top-left,
  &--bottom-left {
    transform: translate3d(calc(-100% - 32px), 0, 0);
  }

  &--top-right,
  &--bottom-right {
    transform: translate3d(calc(100% + 32px), 0, 0);
  }

  &.is-visible {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  &.is-leaving {
    opacity: 0;
  }

  &--top-center {
    &.is-leaving {
      transform: translate3d(0, calc(-100% - 32px), 0);
    }
  }

  &--bottom-center {
    &.is-leaving {
      transform: translate3d(0, calc(100% + 32px), 0);
    }
  }

  &--top-left,
  &--bottom-left {
    &.is-leaving {
      transform: translate3d(calc(-100% - 32px), 0, 0);
    }
  }

  &--top-right,
  &--bottom-right {
    &.is-leaving {
      transform: translate3d(calc(100% + 32px), 0, 0);
    }
  }

  &--success {
    border-color: rgba(58, 148, 98, 0.24);

    .toast__badge,
    .toast__progress {
      background: linear-gradient(135deg, #2c8c5f, #5cc086);
    }
  }

  &--error {
    border-color: rgba(196, 67, 67, 0.24);

    .toast__badge,
    .toast__progress {
      background: linear-gradient(135deg, #bb3434, #df6b6b);
      font-size: x-large;
      color: white;
    }
  }
}

.toast__badge {
  display: flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.toast__icon {
  width: 20px;
  height: 20px;
}

.toast__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.toast__title {
  color: #161616;
  font-size: 14px;
  line-height: 1.15;
}

.toast__message {
  color: #2f2f2f;
  font-size: 14px;
  line-height: 1.35;
}

.toast__close {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b6b6b;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition:
    color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    color: #111;
    transform: scale(1.05);
  }
}

.toast__progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  transform-origin: left center;
  animation-name: toast-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes toast-progress {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}

@media screen and (max-width: 768px) {
  .toast-stack {
    width: calc(100vw - 24px);

    &--top-center {
      top: 12px;
    }

    &--top-left {
      top: 12px;
      left: 12px;
    }

    &--top-right {
      top: 12px;
      right: 12px;
    }

    &--bottom-center {
      bottom: 12px;
    }

    &--bottom-left {
      left: 12px;
      bottom: 12px;
    }

    &--bottom-right {
      right: 12px;
      bottom: 12px;
    }
  }

  .toast {
    padding: 12px;
  }
}
</style>
